import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Loader2, Search, Type, X } from 'lucide-react';
import {
  SYSTEM_BRAND_FONTS,
  ensureFontPreviewLoaded,
  fontStack,
  loadGoogleFontFamilies,
} from '../lib/brandFonts';

type BrandFontPickerProps = {
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
};

export default function BrandFontPicker({
  label,
  hint,
  value,
  onChange,
  placeholder = 'Select a font',
}: BrandFontPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [googleFonts, setGoogleFonts] = useState<string[]>([]);
  const [loadingFonts, setLoadingFonts] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingFonts(true);
    loadGoogleFontFamilies()
      .then((fonts) => {
        if (!cancelled) setGoogleFonts(fonts);
      })
      .finally(() => {
        if (!cancelled) setLoadingFonts(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => searchRef.current?.focus(), 40);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (value.trim()) ensureFontPreviewLoaded(value.trim());
  }, [value]);

  const q = query.trim().toLowerCase();

  const systemMatches = useMemo(() => {
    const list = SYSTEM_BRAND_FONTS;
    if (!q) return list;
    return list.filter((f) => f.toLowerCase().includes(q));
  }, [q]);

  const googleMatches = useMemo(() => {
    if (!q) return googleFonts.slice(0, 80);
    return googleFonts.filter((f) => f.toLowerCase().includes(q)).slice(0, 120);
  }, [googleFonts, q]);

  useEffect(() => {
    if (!open) return;
    const visible = [...systemMatches, ...googleMatches].slice(0, 40);
    visible.forEach((f) => ensureFontPreviewLoaded(f));
  }, [open, systemMatches, googleMatches]);

  const select = (family: string) => {
    ensureFontPreviewLoaded(family);
    onChange(family);
    setOpen(false);
    setQuery('');
  };

  const clear = () => {
    onChange('');
    setQuery('');
  };

  return (
    <div ref={rootRef} className="relative min-w-0">
      <div className="mb-1.5">
        <label className="block text-[13px] font-semibold text-gray-800">{label}</label>
        {hint ? <p className="text-[12px] text-gray-500 mt-0.5">{hint}</p> : null}
      </div>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3.5 py-2.5 rounded-[10px] border border-gray-200 bg-white text-left hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 touch-manipulation"
        style={{ minHeight: 44, WebkitTapHighlightColor: 'transparent' }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Type className="h-4 w-4 text-gray-400 flex-shrink-0" />
        <span
          className={`flex-1 min-w-0 truncate text-[14px] ${
            value ? 'text-gray-900' : 'text-gray-400'
          }`}
          style={value ? { fontFamily: fontStack(value) } : undefined}
        >
          {value || placeholder}
        </span>
        {value ? (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              clear();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                clear();
              }
            }}
            className="p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100"
            aria-label={`Clear ${label}`}
          >
            <X className="h-3.5 w-3.5" />
          </span>
        ) : null}
        <ChevronDown
          className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open ? (
        <div className="absolute z-30 mt-1.5 w-full rounded-[12px] border border-gray-200 bg-white shadow-xl overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search fonts…"
                className="w-full pl-8 pr-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                style={{ fontSize: 16 }}
              />
            </div>
          </div>

          <div
            className="max-h-64 overflow-y-auto py-1"
            style={{ WebkitOverflowScrolling: 'touch' }}
            role="listbox"
          >
            <button
              type="button"
              onClick={() => {
                clear();
                setOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] text-gray-500 hover:bg-gray-50"
            >
              <span className="flex-1">None</span>
              {!value ? <Check className="h-3.5 w-3.5 text-blue-600" /> : null}
            </button>

            {systemMatches.length > 0 ? (
              <>
                <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                  System
                </div>
                {systemMatches.map((family) => (
                  <FontOption
                    key={`sys-${family}`}
                    family={family}
                    selected={value === family}
                    onSelect={select}
                  />
                ))}
              </>
            ) : null}

            <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 flex items-center gap-2">
              <span>Google Fonts</span>
              {loadingFonts ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
            </div>
            {!loadingFonts && googleMatches.length === 0 ? (
              <p className="px-3 py-3 text-[12px] text-gray-400">
                {q ? 'No Google Fonts match that search.' : 'Could not load Google Fonts.'}
              </p>
            ) : (
              googleMatches.map((family) => (
                <FontOption
                  key={`g-${family}`}
                  family={family}
                  selected={value === family}
                  onSelect={select}
                />
              ))
            )}
            {!q && googleFonts.length > 80 ? (
              <p className="px-3 py-2 text-[11px] text-gray-400">
                Showing popular results — type to search all {googleFonts.length.toLocaleString()}{' '}
                Google Fonts.
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function FontOption({
  family,
  selected,
  onSelect,
}: {
  family: string;
  selected: boolean;
  onSelect: (family: string) => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onMouseEnter={() => ensureFontPreviewLoaded(family)}
      onFocus={() => ensureFontPreviewLoaded(family)}
      onClick={() => onSelect(family)}
      className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 ${
        selected ? 'bg-blue-50/70' : ''
      }`}
    >
      <span
        className="flex-1 min-w-0 truncate text-[15px] text-gray-900"
        style={{ fontFamily: fontStack(family) }}
      >
        {family}
      </span>
      {selected ? <Check className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" /> : null}
    </button>
  );
}
